import React, { useState, useEffect } from 'react';
import {
  getCartMessage,
  saveCartMessage,
  getAvailableStores,
  CartMessageData,
  StoreInfo,
} from '../services/cartMessageApiService.ts';
import './CartMessageEditor.css';

interface CartMessageEditorProps {
  onBack: () => void;
}

const CartMessageEditor: React.FC<CartMessageEditorProps> = ({ onBack }) => {
  // Form state
  const [message, setMessage] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [mode, setMode] = useState<'normal' | 'warning'>('normal');
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);

  // Available stores for selection
  const [availableStores, setAvailableStores] = useState<StoreInfo[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [lastEdited, setLastEdited] = useState<Date | null>(null);
  const [lastAuthor, setLastAuthor] = useState<string>('');

  // Load existing cart message and available stores on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load stores and current message in parallel
        const [stores, currentMessage] = await Promise.all([
          getAvailableStores(),
          getCartMessage(),
        ]);

        setAvailableStores(stores);

        if (currentMessage) {
          setMessage(currentMessage.message);
          setIsEnabled(currentMessage.isEnabled);
          setMode(currentMessage.mode);
          setSelectedStoreIds(currentMessage.storeIds);
          setLastEdited(currentMessage.lastEdited);
          setLastAuthor(currentMessage.lastAuthor);
        }
      } catch (err) {
        console.error('Error loading cart message data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Handle store selection toggle
  const handleStoreToggle = (legacyId: string) => {
    setSelectedStoreIds((prev) =>
      prev.includes(legacyId)
        ? prev.filter((id) => id !== legacyId)
        : [...prev, legacyId]
    );
  };

  // Select/deselect all stores
  const handleSelectAll = () => {
    if (selectedStoreIds.length === availableStores.length) {
      setSelectedStoreIds([]);
    } else {
      setSelectedStoreIds(availableStores.map((s) => s.legacyId));
    }
  };

  // Handle save
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      await saveCartMessage({
        message,
        isEnabled,
        mode,
        storeIds: selectedStoreIds,
      });

      setSuccessMessage('Cart message saved successfully!');
      setLastEdited(new Date());

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error saving cart message:', err);
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Group stores by state
  const storesByState = availableStores.reduce((acc, store) => {
    if (!acc[store.state]) {
      acc[store.state] = [];
    }
    acc[store.state].push(store);
    return acc;
  }, {} as Record<string, StoreInfo[]>);

  // State name mapping
  const stateNames: Record<string, string> = {
    MD: 'Maryland',
    CT: 'Connecticut',
    NJ: 'New Jersey',
    RI: 'Rhode Island',
    VT: 'Vermont',
  };

  if (loading) {
    return (
      <div className="cart-message-editor">
        <div className="cart-message-loading">Loading cart message configuration...</div>
      </div>
    );
  }

  return (
    <div className="cart-message-editor">
      <div className="cart-message-header">
        <button className="back-button" onClick={onBack}>
          ← Back
        </button>
        <h2>Cart Message Configuration</h2>
      </div>

      {error && <div className="cart-message-error">{error}</div>}
      {successMessage && <div className="cart-message-success">{successMessage}</div>}

      <div className="cart-message-form">
        {/* Enable/Disable Toggle */}
        <div className="form-section">
          <label className="toggle-label">
            <span className="toggle-text">Enable Cart Message</span>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </div>
            <span className={`toggle-status ${isEnabled ? 'enabled' : 'disabled'}`}>
              {isEnabled ? 'ON' : 'OFF'}
            </span>
          </label>
        </div>

        {/* Message Text */}
        <div className="form-section">
          <label className="form-label">
            Message Text
            <span className="char-count">{message.length}/500</span>
          </label>
          <textarea
            className="message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter your cart message (1-2 sentences)..."
            maxLength={500}
            rows={3}
          />
          <p className="form-hint">
            This message will appear in the cart between the total and pricing type.
          </p>
        </div>

        {/* Mode Selection */}
        <div className="form-section">
          <label className="form-label">Display Mode</label>
          <div className="mode-options">
            <label className={`mode-option ${mode === 'normal' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="mode"
                value="normal"
                checked={mode === 'normal'}
                onChange={() => setMode('normal')}
              />
              <div className="mode-preview normal">
                <span className="mode-name">Normal</span>
                <span className="mode-description">Green/info style</span>
              </div>
            </label>
            <label className={`mode-option ${mode === 'warning' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="mode"
                value="warning"
                checked={mode === 'warning'}
                onChange={() => setMode('warning')}
              />
              <div className="mode-preview warning">
                <span className="mode-name">Warning</span>
                <span className="mode-description">Yellow/alert style</span>
              </div>
            </label>
          </div>
        </div>

        {/* Store Selection */}
        <div className="form-section">
          <div className="store-selection-header">
            <label className="form-label">Display at Stores</label>
            <button
              type="button"
              className="select-all-button"
              onClick={handleSelectAll}
            >
              {selectedStoreIds.length === availableStores.length
                ? 'Deselect All'
                : 'Select All'}
            </button>
          </div>
          <p className="form-hint">
            Select which stores should display this message in their cart.
          </p>

          <div className="store-groups">
            {Object.entries(storesByState).map(([state, stores]) => (
              <div key={state} className="store-group">
                <h4 className="state-header">{stateNames[state] || state}</h4>
                <div className="store-checkboxes">
                  {stores.map((store) => (
                    <label key={store.legacyId} className="store-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedStoreIds.includes(store.legacyId)}
                        onChange={() => handleStoreToggle(store.legacyId)}
                      />
                      <span className="store-name">{store.displayName}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="selection-summary">
            {selectedStoreIds.length} of {availableStores.length} stores selected
          </div>
        </div>

        {/* Last Edited Info */}
        {lastEdited && (
          <div className="last-edited-info">
            Last edited: {lastEdited.toLocaleString()} by {lastAuthor}
          </div>
        )}

        {/* Save Button */}
        <div className="form-actions">
          <button
            className="save-button"
            onClick={handleSave}
            disabled={saving || !message.trim()}
          >
            {saving ? 'Saving...' : 'Save Cart Message'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartMessageEditor;
